import QorusClient from "@/api/clients/QorusClient";
import axios, { AxiosRequestConfig } from "axios";
import { ShoppingCart } from "@/models/shopping";
import { Address } from "@/models/address";
import { Customer } from "@/models/customer";
import store from "@/store";

/**
 *  Qorus API about user's cart
 */
export default class CartApi {

  private cancelSetOfferToken;
  private cancelSetShippingAddressToken;
  private cancelSetShippingMethodToken;
  private cancelGetShippingMethodToken;

  static of(): CartApi {
    return new CartApi();
  }

  getCart(): Promise<ShoppingCart> {
    return QorusClient.client.get("shopping-cart").then(
      (data: ShoppingCart) => {
        return ShoppingCart.of(data);
      },
      (data) => {
        throw data;
      },
    );
  }

  emptyCart(): Promise<null> {
    return QorusClient.client.delete("shopping-cart").then(
      () => {
        return null;
      },
      (data) => {
        throw data;
      },
    );
  }

  addCoupon(coupon: string): Promise<ShoppingCart> {
    const axiosRequestConfig: AxiosRequestConfig = {
      params: {
        coupon,
      },
    };
    return QorusClient.client.put("shopping-cart/set-coupon", {}, axiosRequestConfig).then(
      (data: ShoppingCart) => {
        return ShoppingCart.of(data);
      },
      (data) => {
        throw data;
      },
    );
  }

  removeCoupon(coupon: string): Promise<ShoppingCart> {
    const axiosRequestConfig: AxiosRequestConfig = {
      params: {
        coupon,
      },
    };
    return QorusClient.client.delete("shopping-cart/remove-coupon", axiosRequestConfig).then(
      (data: ShoppingCart) => {
        return ShoppingCart.of(data);
      },
      (data) => {
        throw data;
      },
    );
  }

  setOfferQuantity(params): Promise<ShoppingCart | null> {
    // method: string, offerId: string, addedQuantity: number, currentCart: ShoppingCart | null
    //v1/products?category=<category>
    //v1/products/categories/<category>
    // page = 0, pageSize = 10, sort = "price", asc = true;
    let defaultQuantity = 0;
    let prevQty = 0;
    const cart: ShoppingCart | null = params.currentCart;
    if (cart && cart.cartItems && cart.cartItems.length) {
      const itemIsInCart = cart.cartItems.find(item => item.product?.offer?.id === params.offerId);
      prevQty = itemIsInCart?.quantity || 0;
    }
    switch (params.method) {
      case "add":
        defaultQuantity = Number(params.quantity) + prevQty;
        break;
      case "update" :
        defaultQuantity = Number(params.quantity);
        break;
      case "remove":
        defaultQuantity = 0;
        break;
    }
    if (typeof this.cancelSetOfferToken != typeof undefined) {
      this.cancelSetOfferToken.cancel({ cancel: true });
    }

    this.cancelSetOfferToken = axios.CancelToken.source();

    const axiosRequestConfig: AxiosRequestConfig = {
      params: {
        offerId: params.offerId,
        quantity: Math.round(defaultQuantity),
      },
      cancelToken: this.cancelSetOfferToken.token,
    };

    return QorusClient.client.put("shopping-cart/set-offer-quantity", {}, axiosRequestConfig)
      .then(
        (data: Customer) => {
          if (data) {
            const customer = Customer.of(data);
            store.dispatch('setCustomer', customer);
            return ShoppingCart.of(data.shoppingCart);
          } else {
            return null;
          }
        },
        (data) => {
          throw data;
        },
      );

  }

  setShippingAddress(shippingAddress: Address): Promise<ShoppingCart> | undefined {
      if (typeof this.cancelSetShippingAddressToken != typeof undefined) {
        this.cancelSetShippingAddressToken.cancel({ cancel: true });
      }
      if (typeof this.cancelSetShippingMethodToken != typeof undefined) {
        this.cancelSetShippingMethodToken.cancel({ cancel: true });
      }

      this.cancelSetShippingAddressToken = axios.CancelToken.source();

      const axiosRequestConfig: AxiosRequestConfig = {
        cancelToken: this.cancelSetShippingAddressToken.token,
      };
      return QorusClient.client.put("shopping-cart/shipping-address", shippingAddress, axiosRequestConfig)
        .then(
          (data: ShoppingCart) => {
            return ShoppingCart.of(data);
          },
          (data) => {
            throw data;
          },
        );
  }

  setBillingAddress(billingAddressName): Promise<ShoppingCart> | undefined {
      const axiosRequestConfig: AxiosRequestConfig = {
        params: {
          billingAddressName,
        },
      };
      return QorusClient.client.put("shopping-cart/billing-address", {}, axiosRequestConfig)
        .then(
          (data: ShoppingCart) => {
            return ShoppingCart.of(data);
          },
          (data) => {
            throw data;
          },
        );
  }

  getShipmentMethods(): Promise<Array<unknown>> | undefined {
      if (typeof this.cancelGetShippingMethodToken != typeof undefined) {
        this.cancelGetShippingMethodToken.cancel({ cancel: true });
      }
      if (typeof this.cancelSetShippingMethodToken != typeof undefined) {
        this.cancelSetShippingMethodToken.cancel({ cancel: true });
      }

      this.cancelGetShippingMethodToken = axios.CancelToken.source();
      const axiosRequestConfig: AxiosRequestConfig = {
        cancelToken: this.cancelGetShippingMethodToken.token,
      };
      return QorusClient.client.get("shopping-cart/shipment-methods", axiosRequestConfig)
        .then(
          (data) => {
            return data;
          },
          (data) => {
            throw data;
          },
        );
  }

  setShipmentMethods(selectedShipmentMethods): Promise<ShoppingCart> | undefined {
      if (typeof this.cancelSetShippingMethodToken != typeof undefined) {
        this.cancelSetShippingMethodToken.cancel({ cancel: true });
      }

      this.cancelSetShippingMethodToken = axios.CancelToken.source();

      const axiosRequestConfig: AxiosRequestConfig = {
        cancelToken: this.cancelSetShippingMethodToken.token,
      };
      return QorusClient.client.put("shopping-cart/shipment-methods", { shipmentMethodIds: selectedShipmentMethods }, axiosRequestConfig)
        .then(
          (data: ShoppingCart) => {
            return ShoppingCart.of(data);
          },
          (data) => {
            throw data;
          },
        );
  }
}